import SGLinesComponent from "components/lines/sg-lines-component";
import SGSpinReelTransition from "components/matrix/reel/transition-behaviours/spin/sg-spin-reel-transition";
import SGMatrixComponent from "components/matrix/sg-matrix-component";
import backgroundTint from "components/show-win-lines/background-tint";
import SGFrameSymbolBehaviour from "components/symbol-behaviours/sg-frame-behaviour";
import reelDropComponent, { DropDisplayObject, DropElement } from "goldenrock/components/reeldrop/reel-drop-component";
import GoldenRockInit from "goldenrock/states/golden-rock-init";
import { SGLayers, symbolsName } from "sg-consts";
import { CountMode } from "slotworks/components/bigwin/big-win-component";
import CelebrateWinComponent from "slotworks/components/celebrate-win/celebrate-win-component";
import Components from "slotworks/components/components";
import screenShakeComponent from "slotworks/components/effects/screen-shake-component";
import ReelSpinner from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import ButtonElement from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import loader from "slotworks/loader/loader";
import { Services } from "slotworks/services/services";
import ShowWinLines from "slotworks/state-machine/standard/states/show-win-lines";
import Contract from "slotworks/utils/contracts/contract";
import Sequence from "slotworks/utils/contracts/sequence";
import deviceInfo from "slotworks/utils/device-info";
import sgFreespinTransitionComponent from "components/sg-freespin-transition-component";
import textStylePatch from "utils/text-style-patch";
import SGBackgroundShadeComponent from "components/sg-background-shade-component";
import SGScatterParticles from "components/matrix/scatter/sg-scatter-particles";
import SGWildsComponent from "components/wilds/sg-wilds-component";
import sgScatterAnimation from "components/sg-scatter-animation";
import sgBackgroundParticles from "components/background/sg-background-particles";
import { DisplayObject } from "pixi.js";
import Parallel from "slotworks/utils/contracts/parallel";
import sgSandAnimation from "components/sg-sand-animation";
import Point from "slotworks/utils/geom/point";
import SettingsModel from "slotworks/model/settings/settings-model";

export default class SGInit extends GoldenRockInit {

    private dropDisplayObjects: DropDisplayObject[];
    private hideElements: DropElement[] = [
        { layer: Layers.MatrixBackground },
        { layer: Layers.SymbolAnimations },
        { layer: Layers.CelebrationBackground },
        { layer: Layers.MatrixForeground }
    ];

    public complete(): void {
        //loader.loadStage().execute();

        this.setupScreenShake();
        this.setupBonusTransition();

        if (deviceInfo.isDesktop) {
            this.repositionMenuCloseButton();
        }

        super.complete();
    }

    public onEnter() {
        Components.LinesComponentType = SGLinesComponent;
        Components.MatrixComponentType = SGMatrixComponent;
        Components.SpinReelTransitionType = SGSpinReelTransition;
        super.onEnter();
        (Components.matrix as SGMatrixComponent).LightningLayer = this.createLayer(SGLayers.Lightening);
        (Components.matrix as SGMatrixComponent).LightningLayer.moveAbove(Layers.SymbolAnimations);

        Layers.Custom.get(SGLayers.Sand).moveBehind(Layers.MatrixBackground);
    }

    public onExit() {
        this.showReels(true).execute();
        if (deviceInfo.isIpad()) {
            CanvasManager.setPortraitScale(1, new Point(0.5, 0.31), new Point(0, -0.25));
        }
        super.onExit();
    }

    protected setupComponents(): void {
        super.setupComponents();
        backgroundTint.hide();
        ReelSpinner.anticipationLayer = Layers.Custom.get(SGLayers.Anticipation);
        ShowWinLines.BIG_WINS = true;
        CelebrateWinComponent.ENABLE_TICKUP = false;
        CelebrateWinComponent.ENABLE_PERSISTENT_TOTAL_WIN = true;
        ReelSpinner.blurStartStage = 0;
        CelebrateWinComponent.TOTAL_WIN_THRESHOLD = 5;
        Components.bigWin.countupMode = CountMode.TIERED;
        textStylePatch.init();
        SGBackgroundShadeComponent.init();
        sgScatterAnimation.init();
        SGScatterParticles.init();
        SGWildsComponent.init();
        sgBackgroundParticles.init();
        sgSandAnimation.init();
        Components.matrix.stickyLayer = Layers.Custom.get(SGLayers.Stickies);
        // tslint:disable-next-line:max-line-length
        Components.matrix.addDefaultSymbolBehaviour(SGFrameSymbolBehaviour, [symbolsName.H1, symbolsName.EXPANDED_2, symbolsName.EXPANDED_3, symbolsName.EXPANDED_4, symbolsName.EXPANDED_2_3, symbolsName.EXPANDED_3_4, symbolsName.EXPANDED_2_3_4]);
    }

    protected showOnboard(): Contract<void> {
        return new Sequence([
            () => new Parallel([
                () => this.showReels(false),
                () => super.showOnboard(),
                () => this.showOnboardChineseLogo()
            ]),
            () => Contract.wrap(() => {
                Services.sound.unmute();
            })
        ]);
    }


    private showOnboardChineseLogo(): Contract<void> {
        let onBoardLayer = Layers.Prompts;
        return new Contract((resolve) => {
            const logo = onBoardLayer.getSprite("portrait_" + "logo");
            const zhLogo = onBoardLayer.getSprite("portrait_" + "logo_zh");
            const landscapeLogo = onBoardLayer.getSprite("logo_zh");
            if (logo && zhLogo) {
                if (SettingsModel.language.indexOf("zh") > -1) {
                    logo.visible = false;
                    zhLogo.visible = true;
                    landscapeLogo.visible = true;
                } else {
                    logo.visible = true;
                    zhLogo.visible = false;
                    landscapeLogo.visible = false;
                }
            }
            resolve();
        });
    };

    private showReels(value: boolean): Contract<void> {
        const alpha = value === true ? 1 : 0;
        return new Contract((resolve) => {
            this.getDropDisplayObjects().forEach((element) => {
                element.setVisible(value);
                element.displayObject.alpha = alpha;
            });
            resolve();
        });
    }

    private getDropDisplayObjects() {
        if (!this.dropDisplayObjects) {
            this.dropDisplayObjects = [
                new DropDisplayObject(Layers.MatrixContent.container)
            ];

            this.hideElements.forEach((element: DropElement) => {
                const displayObject: DisplayObject = element.spriteName ?
                    element.layer.getSprite(element.spriteName) :
                    element.layer.container;
                this.dropDisplayObjects.push(new DropDisplayObject(displayObject));
            });
        }

        return this.dropDisplayObjects;
    }

    protected setupLayerOrder(): void {
        super.setupLayerOrder();
        Layers.Footer.moveBehind(Layers.SmartButton);
    }

    private createLayer(layerName: string): Layers {
        const stage: PIXI.Container = CanvasManager.stage;
        const layer: Layers = new Layers(layerName);
        const layerContainer: PIXI.Container = new PIXI.Container();
        layerContainer.name = layer.id;
        layer.container = layerContainer;
        stage.addChild(layerContainer);
        return layer;
    }

    private repositionMenuCloseButton(): void {
        Layers.MenusContent.onSceneEnter.add((scene: string) => {
            if (scene === "gamesettings" || scene === "paytable") {
                const closeButton: ButtonElement = Layers.MenusContent.getButton("close");
                if (closeButton) {
                    closeButton.x = 1456;
                }
            }
        });
    }

    private setupScreenShake(): void {
        screenShakeComponent.init([Layers.CelebrationBackground]);
        screenShakeComponent.layersToShake.push(Layers.Custom.get(SGLayers.Stickies));
        screenShakeComponent.shakeDuration = 2000;
        screenShakeComponent.shakeDomBackground = true;
    }

    private setupBonusTransition(): void {
        sgFreespinTransitionComponent.init([
            {layer: Layers.MatrixBackground},
            {layer: Layers.Banners},
            {layer: Layers.SymbolAnimations},
            {layer: Layers.CelebrationBackground},
            {layer: Layers.MatrixForeground},
            {layer: Layers.Custom.get(SGLayers.Stickies)}
        ]);
    }
}
